import React, { useState, useEffect } from 'react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Calendar as CalendarIcon, X, AlertCircle } from 'lucide-react';
import { addDays, differenceInDays, format, startOfDay, addMonths, isBefore, isAfter } from 'date-fns';
import { es } from 'date-fns/locale';

const MIN_DAYS = 30; // Minimum 1 month (approximately 30 days)

export default function DateRangePicker({ 
  storeName,
  productName,
  quantity = 1,
  initialStartDate,
  initialEndDate,
  onSelectionChange
}) {
  const [dateRange, setDateRange] = useState({
    from: initialStartDate ? new Date(initialStartDate) : undefined,
    to: initialEndDate ? new Date(initialEndDate) : undefined
  });
  const [error, setError] = useState(null);

  // Minimum date is today
  const today = startOfDay(new Date());
  
  // Maximum date is 2 years from now
  const maxDate = addMonths(today, 24);

  useEffect(() => {
    if (initialStartDate && initialEndDate) {
      setDateRange({
        from: new Date(initialStartDate),
        to: new Date(initialEndDate)
      });
    }
  }, [initialStartDate, initialEndDate]);

  const handleSelect = (range) => {
    setError(null);
    
    if (!range) {
      setDateRange({ from: undefined, to: undefined });
      return;
    }

    // If only start date selected, wait for end date
    if (range.from && !range.to) {
      setDateRange({ from: range.from, to: undefined });
      return;
    }

    // Both dates selected - validate minimum duration
    if (range.from && range.to) {
      const daysDiff = differenceInDays(range.to, range.from);
      
      if (daysDiff < MIN_DAYS) {
        setError(`El período mínimo es de ${MIN_DAYS} días (aproximadamente 1 mes)`);
        setDateRange({ from: range.from, to: undefined });
        return;
      }

      setDateRange(range);
      
      // Notify parent with ISO date strings
      onSelectionChange?.({
        startDate: format(range.from, 'yyyy-MM-dd'),
        endDate: format(range.to, 'yyyy-MM-dd'),
        startMonth: format(range.from, 'yyyy-MM'),
        endMonth: format(range.to, 'yyyy-MM')
      });
    }
  };

  const clearSelection = () => {
    setDateRange({ from: undefined, to: undefined });
    setError(null);
    onSelectionChange?.(null);
  };

  const getDuration = () => {
    if (!dateRange.from || !dateRange.to) return null;
    const days = differenceInDays(dateRange.to, dateRange.from);
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    
    if (months === 0) {
      return `${days} días`;
    } else if (remainingDays === 0) {
      return `${months} mes${months > 1 ? 'es' : ''}`;
    } else {
      return `${months} mes${months > 1 ? 'es' : ''} y ${remainingDays} días`;
    }
  };

  return (
    <Card className="border-2 border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-primary" />
            Seleccionar Período de Reserva
          </CardTitle>
          {(dateRange.from || dateRange.to) && (
            <Button variant="ghost" size="sm" onClick={clearSelection} className="text-slate-500">
              <X className="w-4 h-4 mr-1" />
              Limpiar
            </Button>
          )}
        </div>
        {storeName && productName && (
          <p className="text-sm text-slate-500">
            {productName} en {storeName}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Calendar */}
        <div className="flex justify-center">
          <Calendar
            mode="range"
            selected={dateRange}
            onSelect={handleSelect}
            numberOfMonths={2}
            disabled={(date) => isBefore(date, today) || isAfter(date, maxDate)}
            locale={es}
            className="rounded-md border"
            data-testid="date-range-calendar"
          />
        </div>

        {/* Instructions */}
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Nota:</strong> El período mínimo de reserva es de {MIN_DAYS} días (aproximadamente 1 mes).
            Seleccione la fecha de inicio y luego la fecha de fin.
          </p>
        </div>

        {/* Selection Summary */}
        {dateRange.from && dateRange.to && (
          <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-primary">Período seleccionado:</p>
                <p className="text-lg font-bold text-slate-900">
                  {format(dateRange.from, "d 'de' MMMM yyyy", { locale: es })} 
                  {' → '}
                  {format(dateRange.to, "d 'de' MMMM yyyy", { locale: es })}
                </p>
              </div>
              <Badge className="bg-primary text-white">
                {getDuration()}
              </Badge>
            </div>
          </div>
        )}

        {/* Intermediate instruction */}
        {dateRange.from && !dateRange.to && (
          <p className="text-sm text-center text-amber-600 bg-amber-50 p-2 rounded">
            Ahora seleccione la fecha de finalización (mínimo {MIN_DAYS} días después)
          </p>
        )}
      </CardContent>
    </Card>
  );
}
